import { useCallback, useEffect, useMemo, useState } from "react";
import Table from "@/components/Table/table";
import { useCreditorVotesDefs } from "./CreditorVotesDef";
import { useParams } from "react-router-dom";
import {
  useDeleteVoteMutation,
  useGetVotesByBallotIdQuery,
  useLazyGetBallotResultByIdQuery,
} from "./votingReducer";
import {
  MotifProgressLoader,
  MotifButton,
  MotifDropdown,
  MotifDropdownItem,
  MotifFormField,
} from "@ey-xd/motif-react";
import RecordVote from "./RecordVote";
import { useTranslation } from "react-i18next";
import { useLazyExportDocumentQuery } from "@/services/exports/exportsReducer";
import BulkVotingImports from "./VotingForm/BulkVotingImports";
import { useLazyDownloadTemplateDocumentQuery } from "@/services/manualDataSource/ManualDataSourceReducer";
import { useCase } from "@/features/case/use-case";
import { useDispatch, useSelector } from "react-redux";
import { commonFormateName, havingEditPermission } from "@/utility/common";
import {
  CREATE_BUTTON_VARIANT,
  EXPORT_BUTTON_VARIANT,
  GetCommonDeleteImgButton,
  REFRESH_BUTTON_VARIANT,
} from "@/components/Images/CommonImages";
import NotificationGrid from "@/components/Notification/NotificationGrid";
import { customTableLoadingData } from "@/components/Table/CustomPaginationConstants";
import CustomPagination from "@/components/Pagination/CustomPagination";
import { setComponentState1 } from "@/features/retain_state/retainStateSlice";
import { paginationPageSize, paginationPageSizeSelector } from "@/Constant";
import ConfirmationModal from "@/components/Modal/ConfirmationModal/ConfirmationModal";
import { useTrans } from "@/components/Functions/dateFunc";
import SearchInput from "@/components/SearchInput/SearchInput";
import "./CreditorVote.scss";
export default function CreditorVotesTab({
  isReadonly,
  BallotVerionSelect,
  setIsRefreshVersionList,
  ballot,
  readBallot,
}: any) {
  const [isCurrentBallotSelect, setIsCurrentBallotSelect] = useState(true);
  const { editId } = useParams<{
    editId: string;
  }>();
  const caseData = useCase("", true);
  const permissions = useSelector((state: any) => state.permissions);
  const { CreditorVote, ImportJob } = permissions;
  const [errorMsg, setErrorMsg] = useState({ message: "", type: "" });
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedVoterId, setSelectedVoterId] = useState<any>({
    id: "",
    voterId: "",
    voterName: "",
  });
  const [templateDownloadError, setTemplateDownloadError] = useState<
    string | undefined
  >("");
  const { t } = useTranslation();

  const heading = useTrans(
    t,
    t("pages.voting.creditorVotes.confirmationMessage", {
      voterName: selectedVoterId?.voterName,
    }),
    "voterName"
  );
  const dispatch = useDispatch();
  const [getBallotResultById, { isFetching: ballotResultLoading }] =
    useLazyGetBallotResultByIdQuery();
  const retainState = useSelector(
    (e: any) => e.retainState.creditorVotesTabState
  );
  const [editVote, setEditVote] = useState<any>({ show: false, voterId: "" });
  const getPaginationObj: any = useMemo(() => {
    let value: any = {
      ballotId: ballot?.ballot?.id,
      isRefresh: true,
      caseId: caseData.caseId || "",
      PageNumber: retainState?.PageNumber || 1,
      PageSize: retainState?.PageSize,
      SearchText: retainState?.SearchText || "",
    };
    if (retainState?.SortOrder && retainState?.SortColumn) {
      value.SortOrder = retainState?.SortOrder;
      value.SortColumn = retainState?.SortColumn;
    }
    return value;
  }, [caseData?.caseId, retainState, ballot?.ballot?.id]);
  const paginationObj = getPaginationObj;

  const {
    data: votersData,
    isLoading,
    isFetching,
    isError,
    refetch,
    status,
  } = useGetVotesByBallotIdQuery(paginationObj, { skip: !ballot?.ballot?.id });
  const [deleteVote, { isLoading: deleteVoteLoading }] =
    useDeleteVoteMutation();
  let resetSortStatus = useCallback(
    (defaultSort: string) => {
      let json: any = { ...retainState.sortedColumnTypes };
      for (const key in json) {
        json[key] = { sort: null };
      }
      json[defaultSort] = { sort: "asc" };
      return json;
    },
    [retainState.sortedColumnTypes]
  );
  function refreshCreditorVotesList() {
    refetch();
    dispatch(
      setComponentState1({
        entityName: "creditorVotesTabState",
        entityState: {
          PageNumber: 1,
          SearchText: "",
          SortOrder: "asc",
          SortColumn: "VoterName",
          PageSize: paginationPageSize,
          sortedColumnTypes: { ...resetSortStatus("voterNameType") },
        },
      })
    );
  }
  useEffect(() => {
    if (!ballot?.ballot?.id) return;
    refetch();
    if (retainState?.ballotVersionId !== ballot?.ballot?.id) {
      dispatch(
        setComponentState1({
          entityName: "creditorVotesTabState",
          entityState: {
            PageNumber: 1,
            SearchText: "",
            SortOrder: "asc",
            SortColumn: "VoterName",
            PageSize: paginationPageSize,
            sortedColumnTypes: { voterNameType: { sort: "asc" } },
          },
        })
      );
    }
  }, [ballot?.ballot?.id, refetch, dispatch, retainState?.ballotVersionId]);

  function handlePaginationData(data: any, columnTypesData: any) {
    dispatch(
      setComponentState1({
        entityName: "creditorVotesTabState",
        entityState: {
          SortOrder: data.SortOrder,
          SortColumn: data.SortColumn,
          sortedColumnTypes: columnTypesData,
        },
      })
    );
  }
  function refetchCreditorVotesList() {
    readBallot().then(() => {
      dispatch(
        setComponentState1({
          entityName: "creditorVotesTabState",
          entityState: {
            PageNumber: 1,
            SearchText: "",
            SortOrder: "asc",
            SortColumn: "VoterName",
            PageSize: paginationPageSize,
            sortedColumnTypes: { ...resetSortStatus("voterNameType") },
          },
        })
      );
      refetch();
    });
  }
  const triggerGetVotes = () => {
    setErrorMsg({ message: "", type: "" });
    getBallotResultById({ ballotId: editId }).then(async (value) => {
      setIsRefreshVersionList(true);
      refetchCreditorVotesList();
    });
  };

  const TitleComplement = (
    CreditorVotePermisiion: string[],
    ImportJobPermission: string[]
  ) => {
    const { editId } = useParams<{
      editId: string;
    }>();
    const [exportDocument, { isFetching: exportLoading }] =
      useLazyExportDocumentQuery();
    const [showModal, setShowModal] = useState(false);
    const [showBulkUploadModal, setShowBulkUploadModal] = useState(false);
    const [qucikSearchText, setQuickSearchText] = useState(
      paginationObj.SearchText || ""
    );
    const votesTitleMenu1 = [
      {
        label: "pages.voting.creditorVotes.moreOptions.label1",
        value: "Manual Recording",
      },
    ];
    const { caseShortName, abbreviation } = useSelector(
      (state: any) => state.case
    );

    if (havingEditPermission(ImportJobPermission)) {
      votesTitleMenu1.push({
        label: "pages.voting.creditorVotes.moreOptions.label2",
        value: "Bulk Import",
      });
    }
    const [downloadDocument, { isLoading: downloadLoading }] =
      useLazyDownloadTemplateDocumentQuery();
    const [bulkimportFileStatus, setBulkimportFileStatus] = useState("");
    const handleDownloadVotes = (ballotId: any) => {
      setErrorMsg({ message: "", type: "" });
      exportDocument({
        ballotId: ballot?.ballot?.id || ballotId,
        url: "/Ballot/ExportCreditorVotes",
        fileName:
          caseShortName && ballot?.ballot?.name
            ? commonFormateName(
                caseShortName,
                ballot?.ballot?.name,
                abbreviation,
                "BallotCreditors"
              )
            : "",
      })
        .then((response) => {
          if (response?.error && "data" in response.error) {
            try {
              const errObj = JSON.parse(response?.error?.data as string);
              setErrorMsg({
                message: errObj?.message || errObj.title,
                type: "error",
              });
            } catch (error) {
              setErrorMsg({ message: "Something went wrong!!", type: "error" });
            }
          }
        })
        .catch((error: any) => {
          setErrorMsg({ message: "Something went wrong!!", type: "error" });
        });
    };
    const handleDownloadBulkImportTemplate = async () => {
      try {
        await downloadDocument({
          entityType: "4",
          fileName: caseShortName
            ? commonFormateName(
                caseShortName,
                "VoteImportTemplate",
                abbreviation
              )
            : "",
        }).unwrap();
      } catch (error) {
        if (
          error &&
          typeof error === "object" &&
          "data" in error &&
          error?.data
        ) {
          const errObj = JSON.parse((error as any).data);
          return setTemplateDownloadError(errObj?.message || errObj?.title);
        } else setTemplateDownloadError("Something went wrong!!");
      }
    };

    const handleMenuClick = (menuName: string) => {
      if (menuName === "Manual Recording") {
        setShowModal(true);
      } else {
        setShowBulkUploadModal(true);
      }
    };
    useEffect(() => {
      if (BallotVerionSelect && BallotVerionSelect !== "Current Ballot") {
        setIsCurrentBallotSelect(false);
      } else {
        setIsCurrentBallotSelect(true);
      }
    }, []);

    const refachVotesList = () => {
      if (bulkimportFileStatus === "Success") {
        dispatch(
          setComponentState1({
            entityName: "creditorVotesTabState",
            entityState: {
              PageNumber: 1,
              SearchText: "",
              SortOrder: "asc",
              SortColumn: "VoterName",
              PageSize: paginationPageSize,
              sortedColumnTypes: { ...resetSortStatus("voterNameType") },
            },
          })
        );
      }
    };
    return (
      <div
        style={{
          display: "flex",
          gap: "1rem",
          alignItems: "center",
          justifyContent: "end",
        }}
      >
        <MotifFormField>
          <SearchInput
            className="creditor-votes-search"
            value={qucikSearchText}
            onChange={(e) => {
              setQuickSearchText(e.target.value);
            }}
            withOutDebounce={true}
            onClear={() => {
              dispatch(
                setComponentState1({
                  entityName: "creditorVotesTabState",
                  entityState: {
                    PageNumber: 1,
                    SearchText: "",
                    SortOrder: "asc",
                    SortColumn: "VoterName",
                    PageSize: paginationPageSize,
                    sortedColumnTypes: { ...resetSortStatus("voterNameType") },
                  },
                })
              );
              setQuickSearchText("");
            }}
            onEnter={() => {
              dispatch(
                setComponentState1({
                  entityName: "creditorVotesTabState",
                  entityState: {
                    PageNumber: 1,
                    SearchText: qucikSearchText,
                    SortOrder: "asc",
                    SortColumn: "VoterName",
                    PageSize: paginationPageSize,
                    sortedColumnTypes: { ...resetSortStatus("voterNameType") },
                  },
                })
              );
            }}
          ></SearchInput>
        </MotifFormField>

        <MotifButton
          style={{ height: "46px" }}
          variant={EXPORT_BUTTON_VARIANT}
          type="button"
          onClick={() => handleDownloadVotes(editId)}
          loading={exportLoading}
        >
          {t("pages.voting.creditorVotes.export")}
        </MotifButton>
        {havingEditPermission(CreditorVotePermisiion) && (
          <MotifDropdown
            ariaLabelledby="dropdown-trigger-1"
            id="dropdown-1"
            trigger={
              <MotifButton
                style={{ height: "46px" }}
                aria-label="dropdown button"
                id="dropdown-trigger-1"
                type="button"
                variant={CREATE_BUTTON_VARIANT}
                disabled={!isReadonly || !isCurrentBallotSelect}
              >
                {t("pages.voting.creditorVotes.newVote")}{" "}
              </MotifButton>
            }
          >
            <ul role="menu">
              {votesTitleMenu1.map((menuName: any) => {
                return (
                  <li role="menuitem">
                    <MotifDropdownItem
                      indexItem={1}
                      onClick={() => {
                        handleMenuClick(menuName.value);
                      }}
                    >
                      {t(menuName.label)}
                    </MotifDropdownItem>
                  </li>
                );
              })}
            </ul>
          </MotifDropdown>
        )}
        <RecordVote
          showModal={showModal}
          setShowModal={setShowModal}
          readBallot={refetchCreditorVotesList}
          setErrorMsg={setErrorMsg}
        />
        {showBulkUploadModal && (
          <BulkVotingImports
            showModal={true}
            setShowModal={setShowBulkUploadModal}
            title={t("pages.voting.creditorVotes.bulkVoting.title")}
            description={t("pages.voting.creditorVotes.bulkVoting.description")}
            onDownload={handleDownloadBulkImportTemplate}
            downloadLoading={downloadLoading}
            uploadLabel={t("pages.voting.creditorVotes.bulkVoting.uploadLabel")}
            uploadDescription={t(
              "pages.voting.creditorVotes.bulkVoting.uploadDescription"
            )}
            accept=".pdf,.jpg,.jpeg,.doc,.docx"
            maxFiles={1}
            ballotId={editId || ""}
            onClose={() => {
              setShowBulkUploadModal(false);
              refachVotesList();
              setTemplateDownloadError("");
            }}
            setBulkimportFileStatus={setBulkimportFileStatus}
            templateDownloadError={templateDownloadError}
            refreshCreditorVotesList={refreshCreditorVotesList}
          />
        )}
      </div>
    );
  };
  const creditorVotesDefs = useCreditorVotesDefs(caseData);
  let caseRenderers: any = {
    voterIdRenderer: (params: any) => {
      return params?.data?.isEditDelete &&
        havingEditPermission(CreditorVote) &&
        isReadonly &&
        isCurrentBallotSelect ? (
        <span
          style={{ cursor: "pointer" }}
          onClick={() => {
            setErrorMsg({ message: "", type: "" });
            setEditVote({
              show: true,
              voterId: params?.data?.creditorVoteId,
            });
          }}
          className="text-bold-underline"
        >
          {params.data.voterId || ""}
        </span>
      ) : (
        <>{params.data.voterId || ""}</>
      );
    },
    actionRenderer: (params: any) => {
      return params?.data?.isEditDelete &&
        havingEditPermission(CreditorVote) &&
        isReadonly &&
        isCurrentBallotSelect ? (
        <>
          <GetCommonDeleteImgButton
            onClick={() => {
              setShowDeleteModal(true);
              setSelectedVoterId({
                voterId: params?.data?.creditorVoteId,
                voterName: params?.data?.voterName,
              });
            }}
          ></GetCommonDeleteImgButton>
        </>
      ) : (
        <></>
      );
    },
  };

  return (
    <>
      {isLoading ||
      (!isLoading &&
        !isFetching &&
        !ballotResultLoading &&
        !setIsRefreshVersionList) ||
      !ballot?.ballot?.id ? (
        <MotifProgressLoader show variant="default" className="mar-b-20" />
      ) : (
        <>
          {errorMsg.message && (
            <NotificationGrid
              show={true}
              type={errorMsg.type}
              message={errorMsg.message}
              onClose={() => setErrorMsg({ message: "", type: "" })}
            />
          )}
          <div className="ccwp_claim_claimlisttable mar-b-80">
            <Table
              columnTypes={{ ...retainState.sortedColumnTypes }}
              handlePaginationData={handlePaginationData}
              columnDefs={creditorVotesDefs}
              cellRenderers={
                !isFetching && votersData?.results ? votersData.results : []
              }
              caseRenderers={caseRenderers}
              TitleComplement={() => TitleComplement(CreditorVote, ImportJob)}
              title={"pages.voting.creditorVotes.tableTitle"}
              description={"pages.voting.creditorVotes.tableDescription"}
              pagination={false}
              overlayNoRowsTemplate={
                isFetching
                  ? t(customTableLoadingData)
                  : isError
                  ? t("global.globalNotifications.wentWrong")
                  : t("global.globalNotifications.noRecords")
              }
            />
            <CustomPagination
              apiData={votersData}
              apiStatus={status}
              currentpage={paginationObj.PageNumber}
              pageSize={paginationObj.PageSize}
              disableNext={!votersData?.hasMoreRecords}
              onChangePageSize={(page: number) => {
                dispatch(
                  setComponentState1({
                    entityName: "creditorVotesTabState",
                    entityState: {
                      PageSize: page,
                      PageNumber: 1,
                    },
                  })
                );
              }}
              onChangeNextPage={() => {
                dispatch(
                  setComponentState1({
                    entityName: "creditorVotesTabState",
                    entityState: {
                      PageNumber: paginationObj.PageNumber + 1,
                    },
                  })
                );
              }}
              onChangePreviousPage={() => {
                dispatch(
                  setComponentState1({
                    entityName: "creditorVotesTabState",
                    entityState: {
                      PageNumber: paginationObj.PageNumber - 1,
                    },
                  })
                );
              }}
              pageSizeSelector={paginationPageSizeSelector}
            />
          </div>
          <div
            className="button-container sticky-footer pad-t-20"
            style={{
              display: "flex",
              gap: "1rem",
              justifyContent: "flex-end",
            }}
          >
            {havingEditPermission(CreditorVote) && (
              <MotifButton
                onClick={triggerGetVotes}
                size="small"
                type={ballotResultLoading ? "loader" : "button"}
                variant={REFRESH_BUTTON_VARIANT}
                disabled={!isReadonly || !isCurrentBallotSelect}
              >
                {t("global.globalActions.refresh")}
              </MotifButton>
            )}
          </div>
          <RecordVote
            showModal={editVote.show}
            voterId={editVote.voterId}
            setShowModal={() => setEditVote({ show: false, voterId: "" })}
            readBallot={refetchCreditorVotesList}
            setErrorMsg={setErrorMsg}
          />
          <ConfirmationModal
            isVisible={showDeleteModal}
            setIsVisible={setShowDeleteModal}
            cancelBtnRejectHandler={() => {
              setSelectedVoterId({
                voterId: "",
                voterName: "",
              });
              setShowDeleteModal(false);
            }}
            cancelBtnApproveHandler={() => {
              setErrorMsg({
                type: "",
                message: "",
              });
              deleteVote({
                creditorVoteId: selectedVoterId.voterId,
              })
                .then((res: any) => {
                  if (res?.error && res?.error?.status === 400) {
                    setErrorMsg({
                      type: "error",
                      message:
                        res?.error?.data?.message ||
                        t("global.globalNotifications.wentWrong"),
                    });
                  } else if (res?.data) {
                    setErrorMsg({
                      type: "success",
                      message: t("pages.voting.creditorVotes.successMessage"),
                    });
                    refetch();
                  } else {
                    setErrorMsg({
                      type: "error",
                      message: t("global.globalNotifications.wentWrong"),
                    });
                  }
                  setShowDeleteModal(false);
                })
                .catch(() => {
                  setErrorMsg({
                    type: "error",
                    message: t("global.globalNotifications.wentWrong"),
                  });
                });
            }}
            type={3}
            loading={deleteVoteLoading}
            showHeader={false}
            message={heading}
          />
        </>
      )}
    </>
  );
}
